import React, { useEffect,useState } from 'react';
import { connect } from 'react-redux';
import { ReactSVG } from 'react-svg';
import _isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import ZpButton from 'components/zpButton';
import { useCtxHistory } from 'hooks';
import { updateNotifications,updateUserInfo } from 'services/userProfileService';
import { getUserNotifications } from 'services/userService';
import {
  dispatchAlertsIsLoadingSet,
  dispatchAlertsIsSubscribedSet,
  dispatchAlertsRecentPayloadSet,
} from 'store/jobs/actions';
import { openSignUpModal } from 'store/modals/actions';
import { importStatic } from 'utils';
import { POPUP_SURVEY_SOURCE } from 'utils/constant';

import classes from './index.module.css';

const propTypes = {
  type: PropTypes.oneOf(['button', 'jobCard']),
  filterDetail: PropTypes.shape({}),
};

const defaultProps = {
  type: 'button',
  filterDetail: {},
};

const JobBucketAlerts = (props) => {
  const history = useCtxHistory();

  const {
    type = 'button',
    user,
    openSignUpModal,
    filterDetail = {},
    alertsDetail,
    dispatchAlertsRecentPayloadSet,
    dispatchAlertsIsLoadingSet,
    dispatchAlertsIsSubscribedSet,
  } = props;

  const { recentPayload, isLoading, isSubscribed } = alertsDetail;

  const [isUpdated, setIsUpdated] = useState(true);

  const isUserLoggedIn = !!(user && user.isLoggedIn);

  const checkUser = async () => {
    if (isUserLoggedIn) {
      try {
        const userNotifications = await getUserNotifications();
        const { email } = userNotifications?.notificationSettings || {};
        const isUnsubscribedGlobal = email?.optout || false;
        const isUnsubscribedJobs = email?.job?.optout || false;
        const isSubscribedTemp = !isUnsubscribedGlobal && !isUnsubscribedJobs;
        dispatchAlertsIsSubscribedSet({
          isSubscribed: isSubscribedTemp,
        });
      } catch (err) {
        console.error('Error while getting user notifications', err);
      }
    }
    dispatchAlertsIsLoadingSet({
      isLoading: false,
    });
  };

  useEffect(() => {
    checkUser();
  }, []);

  const getFiltersAlertData = () => {
    const { title = '', jobType = [], jobLevels = [], degrees = [] } = filterDetail || {};
    const degreesFiltered = Array.isArray(degrees) ? degrees.filter((item) => item !== 'None') : [];
    return {
      title: title || '',
      jobType: jobType || [],
      jobLevels: jobLevels || [],
      educationLevel: degreesFiltered?.[0] || '',
    };
  };
  
  const _getFilterPayload = () => {
    const filtersAlertData = getFiltersAlertData();
    const {
      title: filterTitle,
      jobType: filterJobType,
      jobLevels: filterJobLevels,
      educationLevel: filterEducationLevel,
    } = filtersAlertData;
    const titleArr = [];
    if (filterTitle.length) {
      titleArr.push({
        originalSearch: filterTitle,
        normalizedTitle: filterTitle,
      });
    }
    return {
      titles: titleArr,
      jobType: filterJobType,
      jobLevels: filterJobLevels,
      educationLevel: filterEducationLevel.toLowerCase(),
      extendedLocations: ['Work From Home'],
    };
  };

  const _updateUserInfo = async () => {
    try {
      const payload = _getFilterPayload();
      const payloadTemp = {
        ...payload,
        extendedLocations: ['Work From Home'],
      };
      dispatchAlertsRecentPayloadSet({
        recentPayload: { ...payloadTemp },
      });
      dispatchAlertsIsLoadingSet({
        isLoading: true,
      });
      await updateUserInfo({ ...payloadTemp });
    } catch (err) {
      console.error('Error while updating user info', err);
    }
    dispatchAlertsIsLoadingSet({
      isLoading: false,
    });
  };

  const _openSignUpModal = () => {
    const skipQuestion = [1];
    const { titles = [], jobLevels = [], educationLevel = '' } = _getFilterPayload();

    let surveyStep = 'step2';
    if (titles?.length) {
      skipQuestion.push(2);
      surveyStep = 'step3';
    }
    if (jobLevels?.length) {
      skipQuestion.push(6);
    }
    if (educationLevel?.length) {
      skipQuestion.push(4);
    }

    const query = {
      survey_step: surveyStep,
      survey: '036',
      skipQuestion,
      src: POPUP_SURVEY_SOURCE.REMOTE_JOBS_PAGE,
    };
    openSignUpModal({
      ...query,
    });
    history.pushQuery(query);
    _updateUserInfo();
  };

  const _toggleNotifications = (needToSubscribe) => {
    if (needToSubscribe) {
      dispatchAlertsIsSubscribedSet({
        isSubscribed: true,
      });
      _updateUserInfo();
      updateNotifications({ 
        emailChannel: {
          job: { optout: false } 
        }
      });
    } else {
      dispatchAlertsIsSubscribedSet({
        isSubscribed: false,
      });
      updateNotifications({ 
        emailChannel: {
          job: { optout: true } 
        }
      });
    }
  };

  const onFilterUpdate = () => {
    const payload = _getFilterPayload();
    if (isUpdated && !_isEqual(payload, recentPayload)) {
      setIsUpdated(false);
    } else if (!isUpdated && _isEqual(payload, recentPayload)) {
      setIsUpdated(true);
    }
  };

  const onAlertClick = () => {
    if (isUserLoggedIn) {
      if (isSubscribed) {
        if (isUpdated) {
          _toggleNotifications(false);
        } else {
          _updateUserInfo();
        }
      } else {
        _toggleNotifications(true);
      }
    } else {
      _openSignUpModal();
    }
  };


  const getContentTypeButton = () => {
    let iconSrc = importStatic('/ui-router/images/icon-notification.svg');
    let btnText = 'Create Alert';
    if (isUserLoggedIn && isSubscribed) {
      if (isUpdated) {
        iconSrc = importStatic('/ui-router/images/icon-notification-off.svg');
        btnText = 'Turn Off Alert';
      } else {
        iconSrc = importStatic('/ui-router/images/icon-notification.svg');
        btnText = 'Update Alert';
      }
    }

    return (
      <div className={classes['create-alert-btn-container']}>
        <ZpButton
          theme="secondary"
          icon={<ReactSVG className={classes['create-alert-btn-icon']} src={iconSrc} />}
          className={`${classes['create-alert-btn']} ${isLoading ? classes['disabled'] : ''}`}
          onClick={onAlertClick}
          disabled={isLoading}
        >
          {btnText}
        </ZpButton>
      </div>
    );
  };

  const getContentTypeJobCard = () => {
    let cardText = 'Get remote jobs notification on your email';
    if (isUserLoggedIn && isSubscribed) {
      if (isUpdated) {
        cardText = 'Turn off alert for current chosen options';
      } else {
        cardText = 'Update alert for current chosen options';
      }
    }
    return (
      <div className={classes['create-alert-card-container']}>
        <div className={classes['create-alert-card']}>
          <div className={classes['create-alert-card-text']}>{cardText}</div>
          {getContentTypeButton()}
        </div>
      </div>
    );
  };

  const getContent = () => {
    let result = '';
    if (type === 'button') {
      result = getContentTypeButton();
    } else if (type === 'jobCard') {
      result = getContentTypeJobCard();
    }
    return result;
  };

  onFilterUpdate();

  return getContent();
};

const mapStateToProps = ({ user, jobs }) => ({
  user,
  alertsDetail: jobs.alertsDetail,
});

const mapDispatchToProps = {
  openSignUpModal,
  dispatchAlertsRecentPayloadSet,
  dispatchAlertsIsLoadingSet,
  dispatchAlertsIsSubscribedSet,
};

JobBucketAlerts.defaultProps = defaultProps;
JobBucketAlerts.propTypes = propTypes;

export const JobBucketAlertsComponent = JobBucketAlerts;

export default connect(mapStateToProps, mapDispatchToProps)(JobBucketAlerts);
